import { createClient } from 'contentful'
import * as contentTypes from './contentTypes'
import { removeTrailingSlash } from '../util/urlTransforms'

import { graphql } from 'gatsby'

export const locale = process.env.GATSBY_LOCALE || 'en-US'

export const client = createClient({
  space: process.env.GATSBY_SPACE_ID,
  accessToken: process.env.GATSBY_ACCESS_TOKEN
})

let entries = []
let assets = []

let locActParentPages = []
let blogArchivePage = []

export const getImageUrl = (assetId) => {
  return getAsset(assetId).then(asset => asset.fields)
}

export const getLocales = () => client.getLocales().then(result => result.items.map(({ code }) => code))
export const getCurrentLocale = () => locale
const defaultLocale = 'en-US'
export const locales = ['sv-SE', 'da-DK', 'en-US', 'nb-NO', 'de-DE', 'de-CH', 'de-AT', 'fr-FR'] // TODO: change to data from contentful

const localeFallbacks = {
  'sv-SE': defaultLocale,
  'da-DK': defaultLocale,
  'en-US': null,
  'nb-NO': defaultLocale,
  'de-DE': defaultLocale,
  'de-CH': 'de-DE',
  'de-AT': 'de-DE',
  'fr-FR': defaultLocale
}


/* TODO: TODO TODO TODO
TODO Look at why norway surf page has faulty data resolved
*/

export const localizedFieldWithFallback = (fields, fieldName, currentLocale) => {
  // field does not exist, return null
  if (typeof fields[fieldName] === 'undefined' || fields[fieldName] === null)
    return null

  if(typeof fields[fieldName][currentLocale] !== 'undefined') {
    return fields[fieldName][currentLocale]
  }

  // check if we have a proper fallback value, recursively
  let fallbackLocale = localeFallbacks[currentLocale]
  let fallbackFieldValue = fallbackLocale ? localizedFieldWithFallback(fields, fieldName, fallbackLocale) : null

  if(fallbackFieldValue)
    return fallbackFieldValue

  // we didn't, so go on
  // is current and fallback locale value missing but other locale is set? return null
  if (typeof fields[fieldName] === 'object') {
    for (const localeName of locales) {
      if (localeName in fields[fieldName])
        return null
    }
  }

  // not localized values, return actual field, unless empty object (return null then)
  return Object.keys(fields[fieldName]).length || typeof fields[fieldName] !== 'object' ? fields[fieldName] : null
}

export const localizedField = (fields, fieldName) => {
  return localizedFieldWithFallback(fields, fieldName, locale)
}

function localizeFields(fields) {
  const localizedFields = {}

  for (const field in fields) {

    // do not overwrite this, we want to keep all locales to build hraflang link
    if (field === 'slugs') {
      localizedFields[field] = fields[field]
      continue
    }

    // get the localized or fallback value for the fields
    localizedFields[field] = localizedField(fields, field)

    // save a raw copy of the field slug, i.e a copy containing the slug for all languages that we use for hreflang links
    if (field === 'slug' && !localizedFields.slugs) {
      localizedFields.slugs = fields.slugs || fields.slug // keep slug for all locales too
    }

    // keep a raw version of hideForThisLocale it is later used to double check that a value in slugs indeed should generate a hreflang link
    if( field === 'hideForThisLocale' ) {
      localizedFields.hideForThisLocaleRaw = fields[field]
    }

  }

  return localizedFields
}

function localizeAssetOrEntryFields(assetOrEntry) {
  assetOrEntry.fields = localizeFields(assetOrEntry.fields)
  return assetOrEntry
}

const checkIfChildOf = (childEntryId, contentType) => client.getEntries({
  'links_to_entry': childEntryId,
  'content_type': contentType
})
.then(result => (result.items || []).length > 0)

const getSubpagePath = ( subPageId, contentType ) => client.getEntries({
  'content_type': contentType,
  'fields.subPages.sys.id': subPageId,
  'locale': '*'
}).then( result => {
  if( result.total > 0 ) {
    return localizeFields( result.items[0].fields, 'slug' )
  }
  return 0
})


export const resolveEntry = async entry => {

  if (!entry) {
    return null
  }   

  // contentful rich text is in raw json format. 
  // we must do some parsing for inline links to an entry (entry-hyperlink)
  // should the link have a prefix?
  // target.fields contains the full entry that is linked. and becuase of that we run into maximum call stack error if there are circular links. the error happens in the @contentful plugin somehere after calling createPage
  // but if we remove all fields and insert targetSlug and targetSlugPrefix the build does not fail  
  // HOOOWEVER. When an entry-hyperlink in the content field of a blogpost all of a sudden target.fields does not exists
  // UPDATE: if we strip all fields then we run into another problem and that is that an internalLink no longer work under certain circumastances.
  // If we have a locActPage that is being linked to from an Item: Link and an entry-hyperlink in a text then Link.js does not produce a correct link.
  // Why? Not 100% sure but must be becuase the way we resolve content and in the end both Item: Link and entry-hyperlink points to the same entry that we modify. 
  // So in other words when we find an entry-hyperlink we can safely remove .block .subPages & subPagesSpecial as that is what can create the circular references that makes the plugin run into maximum call stack error
  if( entry.nodeType && entry.nodeType == "document") {

    // loop through the content of the document
    entry.content.map( (content) => {

      content.content.map( (c) => {

        // and if it is an entry-hyperlink we must hack-a-way to 1: prevent the build from crashing (maximum callstack) 2: setup the links so that we can use them in contentfulRichText
        // We only care about the localized link as it is an internal link to an entry on the current site
        if( c.nodeType == "entry-hyperlink" ) {

          // this is the id of the page we are linking to
          let targetID = c.data.target.sys.id

          // we are here during run time. no need to resolve entry-hyperlink at run time
          // we end up here when getBlogPosts is called during run time in order to show the last 3 posts
          // and we don't need to resolve the content of those post
          if( !entries.length && typeof window !== 'undefined' ) {
            return null
          }

          // not sure why, but when using an entry-hyperlink in the content field of a blog post target fields is empty and we only have the target id.
          // so let's not rely on fields being present and instead find the slug of the target in the entries list
          let targetEntry = entries.filter( e => {
            return e.sys.id == targetID
          })

          // If this happens during build time then there is a real error.
          // If we hit this during run time we don't need to worry.
          // For instance in BlogPost.js we call getBlogPosts to get the 3 latest "related" posts... 
          // Once they are downloaded they are resolved again. But now we are not in the build so our
          // global list with all entries does not exist... so we will never find it.
          // But we don't need to resolve the entry-link since the link that is being resolved is 
          // in the latest blog post i.e not the blog post we are reading. And once you visit
          // the blog post then it's opened from disk where we already have resolved it...
          if( !targetEntry.length ) {
            return null
          }

          targetEntry = targetEntry[0]

          // this is the slug of the target. if the target is a blogpost or a locActPage a slug prefix might be needed
          let targetSlug = localizedField( targetEntry.fields, 'slug' )

          // slug prefix
          let targetSlugPrefix = ""

          // is it a link to a locActPage ?
          // then we have to check if the locActPage we are linking to is a SubPage or a SubPageSpecial. In that case we also use the slug for the parent 
          if( targetEntry.sys.contentType.sys.id == contentTypes.PAGE_LOC_ACT ) {

            if( !locActParentPages.length ) {
              // we find all locActPages that have either supPages and/or supPagesSpecial
              locActParentPages = entries.filter( e => {
                return e.sys.contentType.sys.id == contentTypes.PAGE_LOC_ACT && ( e.fields.subPages || e.fields.subPagesSpecial )
              })
            }

            // if we find the target among the locActParentPages then we know it is a subpage and we must add a slugPrefix

            for( let i = 0; i < locActParentPages.length; i++ ) {

              let subPages = locActParentPages[i].fields.subPages ? localizedField( locActParentPages[i].fields, 'subPages') : [] 
              let subPagesSpecial = locActParentPages[i].fields.subPagesSpecial ? localizedField( locActParentPages[i].fields, 'subPagesSpecial') : [] 
              let allSubPages = [ ...subPages, ...subPagesSpecial ]

              let found = false

              // look for the target
              for( let j = 0 ; j < allSubPages.length; j++ ) {
                if( allSubPages[j].sys.id == targetID ) {
                  found = true
                  targetSlugPrefix = localizedField( locActParentPages[i].fields, 'slug' )
                  break
                }
              }

              // save an iteration or two
              if( found ) {
                break
              }

            } // end: if( c.data.target.sys.contentType.sys.id == contentTypes.PAGE_LOC_ACT ) {

          }

          if( targetEntry.sys.contentType.sys.id == contentTypes.BLOG_POST ) {

            // find the blogArchive so we can get the translated slug
            if( !blogArchivePage.length ) {
              blogArchivePage = entries.filter( e => {
                return e.sys.contentType.sys.id == contentTypes.PAGE_BLOG_ARCHIVE 
              })              
            }

            targetSlugPrefix = localizedField( blogArchivePage[0].fields, 'slug' )

          }

          // keep old fields if they exist (except for blocks, subPages and subPagesSpecial) and add the targetSlug and targetSlugPrefix for when rendering contentful rich text
          c.data.target.fields = c.data.target.fields || {}
          c.data.target.fields.blocks = null
          c.data.target.fields.subPages = null
          c.data.target.fields.subPagesSpecial = null
          c.data.target.fields.targetSlug = targetSlug
          c.data.target.fields.targetSlugPrefix = targetSlugPrefix
        
        } 
      })

    })

  }

  
  // Is this entry a link?
  if (entry.sys && entry.sys.type === 'Link') {

    // Is this a link to an Entry or an Asset?
    if (entry.sys.linkType === 'Entry') {

      // Skip the catch to increase fetch speed (which in turn affects build speed for the site)
      //const childEntry = await client.getEntry(entry.sys.id, { locale }).catch((error) => console.log(error));
      const childEntry = getEntry(entry.sys.id)

      // If the child entry can't be found it is probably a draft or similar. Simply return nothing.
      if (!childEntry || childEntry.fields.hideForThisLocale) {
        return Promise.resolve(undefined)
      }

      // Set the content type as a top property as fields and sys will be abstracted away
      childEntry.fields.contentType = childEntry.sys.contentType.sys.id

      // Recursively resolve links for child entry fields
      return await resolveLinksForEntryFields(childEntry.fields)

    } else if (entry.sys.linkType === 'Asset') { // This is a link to a single asset, simply get it
      const asset = await getAsset(entry.sys.id).catch((error) => {
        console.log(error)
      })
      return asset ? { ...asset.fields, assetId: entry.sys.id } : undefined
    }
  } else if (entry.sys && entry.sys.type === 'Asset') { // This is an already resolved asset, just return it
    entry = localizeAssetOrEntryFields(entry)
    return Promise.resolve({ ...entry.fields, assetId: entry.sys.id })
  } else if (entry.fields) { // This entry has already been resolved, just return its fields

    // In the case that the fields does not contain the content type, append it
    if (!entry.fields.contentType) {
      entry.fields.contentType = { [defaultLocale]: entry.sys.contentType.sys.id }
    }

    if (entry.fields.hideForThisLocale && entry.fields.hideForThisLocale[locale]) {
      return Promise.resolve(undefined)
    }
  
    return await resolveLinksForEntryFields(localizeAssetOrEntryFields(entry).fields)
  }

  return Promise.resolve(entry)
}

const transformToLink = entry => {
  if (entry.sys.type === 'Link') {
    return entry
  }

  return { sys: { id: entry.sys.id, type: 'Link', linkType: entry.sys.type } }
}

const resolveFallbackValueForMissingSlugs = (slugs, id) => {

  if( !slugs[ defaultLocale ] ) {
    console.error( "Entry: " + id + " does not have a slug set for default locale " + defaultLocale, slugs )
    return slugs
  }

  // loop the locales array to make sure we set a slug for each language 
  // since de-DE comes before de-AT & de-CH a missing slug for de-DE will be set before de-AT and/or de-CH gets the slug from de-DE i.e their fallback locale
  for (const locale of locales) {

    if( locale != defaultLocale && !slugs.hasOwnProperty( locale ) ) {
      slugs[ locale ] = slugs[ localeFallbacks[ locale ] ]
    }

  }

  return slugs
}

const resolveLinksForEntryFields = async (fields, resolvePageContent = false ) => {
  const resolvedEntry = {}

  fields = localizeFields(fields)
  
  for (const field in fields) {
    let value = fields[field]

    // in contentful some entries have a trailing slash in their slug.
    // let's remove them 
    if ( field == 'slug' ) {
      value = removeTrailingSlash( value )
    }

    // the field slugs is created in localizeFields and is a raw copy of slug (before localization)
    // we use slugs to create the hreflang links (but make sure there are no trailing slashes in the slug)
    if ( field === 'slugs' && value ) {

      for( const key in value ) {
        value[key] = removeTrailingSlash( value[key] )
      }
      // then make sure that we have a slug for each domain... i.e if it is not set in contentful use the fallback language
      resolvedEntry[field] = resolveFallbackValueForMissingSlugs( value, fields.id )
      continue
    }

    // in localizeField we create a raw copy of the field hideForThisLocale that we need later to make sure
    // we are not creating hreflang links to hidden content
    // i.e if de-AT has a slug set but also have hideForThisLocale set to true then we should not create a 
    // hreflang link from the other languages
    if ( field == 'hideForThisLocaleRaw' && !resolvedEntry['hideForThisLocaleRaw'] ) {
      resolvedEntry['hideForThisLocaleRaw'] = value
      continue
    }

    // If set, do not resolve page links, but leave a link to resolve it at a later time
    if (!resolvePageContent && fields.slug && value && (value.sys || (value[0] && value[0].sys))) {
      resolvedEntry[field] = Array.isArray(value) ? value.map(transformToLink) : transformToLink(value)
    } else if (Array.isArray(value)) {
      resolvedEntry[field] = (await Promise.all(value.map(resolveEntry))).filter(Boolean)
    } else {
      resolvedEntry[field] = await resolveEntry(value)
    }
  }
  
  // only keep slugs where content is not hidden for a given locale
  if ( resolvedEntry.slugs && resolvedEntry.hideForThisLocaleRaw )  {

    let filteredSlugs = Object.keys(resolvedEntry.slugs).filter(Boolean).filter( key => {
      if( resolvedEntry.hideForThisLocaleRaw[key] ) {
        return false
      }
      return true
    } ).reduce((cur,key) => { return Object.assign(cur, { [key]: resolvedEntry.slugs[key] })}, {})
    

    resolvedEntry.slugs = filteredSlugs
  }

  return resolvedEntry
}

const removeHiddenPages = ( pages ) => {
  return pages.filter(page => {
    return page && !(!!page.hideForThisLocale && page.hideForThisLocale) // filter out pages that should be hidden for this locale
  }).filter(Boolean) // Make each page truthy or falsy and filter away falsy ones
}

const getEntriesByContentType = (contentType, limit, localeOnly, resolvePageContent = false) => {

  return client.getEntries({
    'content_type': contentType,
    locale: '*',
    include: 4,
    limit: limit,
    //'fields.hideForThisLocale[ne]': localeOnly // << is not the right way to only get entries for a given locale. 
    // removeHiddenPages removes the ones that should not be part of the build
  })
  .then(result => {
    if (result.sys.type === 'Array' && result.total > 0) {
      //return Promise.all(result.items.map(entry => resolveLinksForEntryFields(entry.fields, resolvePageContent)))
      return Promise.all(result.items.map(( { fields, sys: { id } } ) => {
        return resolveLinksForEntryFields( {...fields, id}, resolvePageContent )
      }))
    }

    return []
  })

}

const getSingleEntryByContentType = contentType => getEntriesByContentType(contentType, 1, undefined, true)
.then(entries => entries[0])

const getEntriesThroughPagination = async query => {
  const limit = 10
  let skip = 0
  let items = []

  while(true) {
    const result = await client.getEntries({
      ...query,
      limit: limit,
      skip: skip
    })

    if (Array.isArray(result.items)) {
      items = items.concat(result.items)
      skip += result.items.length
    } else {
      break
    }

    if (items.length === result.total) break
  }
  return items
}

export const getNavigationPrimary = () => getSingleEntryByContentType(contentTypes.NAVIGATION_PRIMARY)

export const getPages = () => { 
  return getEntriesByContentType(contentTypes.PAGE, undefined, true, true)
  .then( pages => removeHiddenPages( pages ) )
}

export const getLocActPages = () =>
  getEntriesThroughPagination({
    'content_type': contentTypes.PAGE_LOC_ACT,
    locale: '*',
    include: 4,
    'fields.slug[exists]': true
  })
  .then(locActPages =>  // Filter away loc act pages that has parent pages
    Promise.all(locActPages.map(page =>
      checkIfChildOf(page.sys.id, page.sys.contentType.sys.id)
      .then(isChild => !isChild && resolveLinksForEntryFields({...page.fields, id: page.sys.id}, true))
    ))
  )
  .then( locActPages => removeHiddenPages( locActPages ) )
  .then(locActPages => Promise.all(locActPages // For each loc act page, resolve its sub pages
    .map(locActPage => Promise.all((localizedField(locActPage, 'subPages') || []) 
      .map(subPage => {
          return resolveLinksForEntryFields(subPage, true)
        }
      ))
      .then(resolvedSubPages => ({ ...locActPage, subPages: resolvedSubPages })) // Return the loc act pages together with resolved subpages
    )
  ))
  .then(locActPages => Promise.all(locActPages // For each loc act page, resolve its sub pages special
    .map(locActPage => Promise.all((localizedField(locActPage, 'subPagesSpecial') || []) 
      .map(subPage => {
          return resolveLinksForEntryFields(subPage, true)
        }
      ))
      .then(resolvedSubPages => ({ ...locActPage, subPagesSpecial: resolvedSubPages })) // Return the loc act pages together with resolved special subpages (sub pages that are not show in the content selector menu)
    )
  ))

export const getLocationPages = () => getEntriesByContentType(contentTypes.PAGE_LOCATION, undefined, true, false)
  .then( pages => removeHiddenPages( pages ) )

export const getActivityPages = () => getEntriesByContentType(contentTypes.PAGE_ACTIVITY, undefined, true, false)
  .then( pages => removeHiddenPages( pages ) )

export const getBlogArchive = () => getSingleEntryByContentType(contentTypes.PAGE_BLOG_ARCHIVE)

export const getBlogCategories = () => client.getEntries({
  'content_type': contentTypes.BLOG_CATEGORY,
  include: 0,
  locale
})
.then(result => {
  if (result.sys.type === 'Array' && result.total > 0) {
    return result.items.map(page => ({ ...page.fields, id: page.sys.id }))
  }

  return []
})


// By default Contentful returns 100 entries if limit has not been defined
// So let's recursively download until we have all posts rather than asking for
// 500 posts or 1000 post as we then might have to adjust that in the future
export const getBlogPosts = async (limit = undefined, skip = 0, categoryId) => {

  let entries = []
  let total = 0
  let done = false

  while( !done ) {
    let result = await client.getEntries({
      'content_type': contentTypes.BLOG_POST,
      include: 0,
      skip: skip,
      limit: limit,
      'links_to_entry': categoryId,
      locale: '*',
    })

    entries = [...entries, ...result.items]    
    total += result.items.length

    if( total < result.total ) {
      skip += result.limit
    }

    done = total >= result.total

    // if called with a limit we should stop fetching entries
    if( limit && limit == total ) {
      done = true
    }

  }

  // all entries are downloaded 
  return Promise.all(entries.map(({ fields, sys: { id, updatedAt, createdAt } }) => {
    // resolve links and localize fields
    return resolveLinksForEntryFields({ ...fields, updatedAt, createdAt, id }, true)
  })).then( result => removeHiddenPages( result ) )

}

export const getPageSettings = () => getSingleEntryByContentType(contentTypes.PAGE_SETTINGS)
export const getCheckoutSettings = () => getSingleEntryByContentType(contentTypes.CHECKOUT_SETTINGS)

export const downloadAllEntries = async () => {
  let limit = 600
  let total = 0
  entries = []
  let done = false

  while (!done) {
    let result = await client.getEntries({
      include: 0,
      skip: total,
      locale: '*',
      limit
    })
    entries = [...entries, ...result.items.filter(entry => {
      return !(entry.fields.hideForThisLocale && entry.fields.hideForThisLocale === 'Hide')
    })]

    total += result.items.length
    done = total >= result.total
  }

  return entries
}

const _downloadAllAssets = async () => {
  let limit = 800
  let total = 0
  let skip = 0
  assets = []
  let done = false

  while (!done) {
    let result = await client.getAssets({
      include: 0,
      skip: total,
      locale: '*',
      limit
    })
    assets = [...assets, ...result.items]
    total += result.items.length
    done = total >= result.total
  }

  return assets
}

let downloadPromise = null

export const downloadAllAssets = () => {
  if (downloadPromise)
    return downloadPromise

  return downloadPromise = _downloadAllAssets()
}

export const syncAll = async () => {
  assets = []
  entries = []

  let result = await client.sync({
    initial: true,
    paginate: false
  })

  assets = result.assets

  return { assets: result.assets, entries: result.entries }
}

export const getEntry = (id) => {
  let entry = entries.filter(entry => entry.sys.id == id)[0]
  return entry
}

export const getAsset = (id) => {
  // check if we already have asset downloaded
  let filtered = assets.filter(entry => entry.sys.id == id)
  return new Promise((resolve, reject) => {
    if (filtered.length) {
      resolve(localizeAssetOrEntryFields(filtered[0]))
    } else {
      // and otherwise, download all assets, store, and resolve asset
      downloadAllAssets().then((allAssets) => {
        filtered = allAssets.filter(entry => entry.sys.id == id)
        //console.log("using cached assets: " + cachedAssetCounter++)
        resolve(filtered[0])
      })
    }
  })
}

// get room galleris for the select rooms step in the checkout
export const getCheckoutRoomSelectGalleries = () => client.getEntries({
  'content_type': contentTypes.ROOM_SELECT_GALLERY,
})
.then(result => {
  if (result.sys.type === 'Array' && result.total > 0) {
    return Promise.all(result.items.map(entry => resolveLinksForEntryFields(entry.fields, false)))
  }

  return []
})

// get data for custom ordering additions in the duration activity select step in the checkout
export const getCustomOrderAdditions = () => client.getEntries({
  'content_type': contentTypes.CUSTOM_ORDER_ADDITIONS,
})
.then(result => {
  if (result.sys.type === 'Array' && result.total > 0) {
    return Promise.all(result.items.map(entry => resolveLinksForEntryFields(entry.fields, false)))
  }

  return []
})
